import {
  useGlobal,
  // useRoadMap,
  VojomoLoader,
  useAccount
} from "@vojomo-core/shared";
import { useEffect, useMemo, useState } from "react";
import { Collapse } from "antd";
import type { CollapseProps } from "antd";
import {
  Rocket,
  Star,
  Compass,
  Target,
  ChevronRight,
  ChevronDown
} from "lucide-react";
import { ReadOnlyEditor } from "components/ReadyOnlyEditor";
import { ObjectivesTable } from "components/ObjectivesTable/ObjectivesTable";

export const RoadmapAtAGlance = () => {
  const { view, setView, buildingBlocksDict, getAllBuildingBlocks, isMobile } =
    useGlobal();
  // const { getRoadmapDates, roadmapDates } = useRoadMap();
  const { companyName, isUpdatingCompany } = useAccount();

  const [mission, setMission] = useState<string>(null);
  const [vision, setVision] = useState<string>(null);
  const [peakAmbition, setPeakAmbition] = useState<string>(null);
  const [peakObjectives, setPeakObjectives] = useState<string>(null);
  const [threeYearObjectives, setThreeYearObjectives] = useState<string>(null);
  const [oneYearObjectives, setOneYearObjectives] = useState<string>(null);
  const [qObjectives, setQObjectives] = useState<string>(null);

  const getAllRoadmapContent = async () => {
    setVision(buildingBlocksDict[view]["Vision"]["text"]);
    setMission(buildingBlocksDict[view]["Mission"]["text"]);
    setPeakAmbition(buildingBlocksDict[view]["Peak Ambition"]["text"]);
    setPeakObjectives(
      buildingBlocksDict[view]["Peak Ambition Objectives"]["text"]
    );
    setThreeYearObjectives(
      buildingBlocksDict[view]["3 Year Objectives"]["text"]
    );
    setOneYearObjectives(buildingBlocksDict[view]["1 Year Objectives"]["text"]);
    setQObjectives(buildingBlocksDict[view]["Quarterly Objectives"]["text"]);
  };

  useEffect(() => {
    if (!view) {
      setView("my");
    }
  }, [view]);

  // useEffect(() => {
  //   getRoadmapDates();
  // }, [view, companyName]);

  useEffect(() => {
    getAllBuildingBlocks(true);
  }, [companyName]);

  useEffect(() => {
    if (buildingBlocksDict) {
      getAllRoadmapContent();
    }
  }, [buildingBlocksDict, view, companyName]);

  const shouldDisplayLoader = useMemo(() => {
    return isUpdatingCompany || !buildingBlocksDict;
  }, [isUpdatingCompany, buildingBlocksDict]);

  const strategicItems: CollapseProps["items"] = [
    {
      key: "strategic",
      label: (
        <span style={{ fontWeight: 600, fontSize: isMobile ? "16px" : "20px" }}>
          Vision, Mission & Peak Ambition
        </span>
      ),
      children: (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: isMobile ? "16px" : "24px",
            padding: isMobile ? "12px" : "16px"
          }}
        >
          {/* Vision Card */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "8px" : "12px",
                marginBottom: isMobile ? "8px" : "12px"
              }}
            >
              <Compass
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  color: "#5B4ECF"
                }}
              />
              <h3
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: 600,
                  margin: 0,
                  padding: 0,
                  height: "100%"
                }}
              >
                Vision
              </h3>
            </div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                color: "#374151",
                lineHeight: "1.6"
              }}
            >
              <ReadOnlyEditor content={vision || "No vision yet..."} />
            </div>
          </div>

          {/* Mission Card */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "8px" : "12px",
                marginBottom: isMobile ? "8px" : "12px"
              }}
            >
              <Target
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  color: "#5B4ECF"
                }}
              />
              <h3
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: 600,
                  margin: 0,
                  padding: 0,
                  height: "100%"
                }}
              >
                Mission
              </h3>
            </div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                color: "#374151",
                lineHeight: "1.6"
              }}
            >
              <ReadOnlyEditor content={mission || "No mission yet..."} />
            </div>
          </div>

          {/* Peak Ambition Card */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: isMobile ? "8px" : "12px",
                marginBottom: isMobile ? "8px" : "12px"
              }}
            >
              <Star
                style={{
                  fontSize: isMobile ? "18px" : "20px",
                  color: "#5B4ECF"
                }}
              />
              <h3
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: 600,
                  margin: 0,
                  padding: 0,
                  height: "100%"
                }}
              >
                Peak Ambition
              </h3>
            </div>
            <div
              style={{
                whiteSpace: "pre-wrap",
                color: "#374151",
                lineHeight: "1.6"
              }}
            >
              <ReadOnlyEditor
                content={peakAmbition || "No peak ambition yet..."}
              />
            </div>
          </div>
        </div>
      )
    }
  ];

  const nestedObjectivesItems: CollapseProps["items"] = [
    {
      key: "peakObjectives",
      label: (
        <span style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>
          Peak Ambition Objectives
        </span>
      ),
      children: (
        <div
          style={{
            padding: isMobile ? "12px" : "16px",
            whiteSpace: "pre-wrap",
            color: "#374151",
            lineHeight: "1.6"
          }}
        >
          <ObjectivesTable text={peakObjectives} emptyMessage="No objectives yet..." />
        </div>
      )
    },
    {
      key: "threeYear",
      label: (
        <span style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>
          3 Year Objectives
        </span>
      ),
      children: (
        <div
          style={{
            padding: isMobile ? "12px" : "16px",
            whiteSpace: "pre-wrap",
            color: "#374151",
            lineHeight: "1.6"
          }}
        >
          <ObjectivesTable text={threeYearObjectives} emptyMessage="No 3 year objectives yet..." />
        </div>
      )
    },
    {
      key: "oneYear",
      label: (
        <span style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>
          1 Year Objectives
        </span>
      ),
      children: (
        <div
          style={{
            padding: isMobile ? "12px" : "16px",
            whiteSpace: "pre-wrap",
            color: "#374151",
            lineHeight: "1.6"
          }}
        >
          <ObjectivesTable text={oneYearObjectives} emptyMessage="No 1 year objectives yet..." />
        </div>
      )
    },
    {
      key: "quarterly",
      label: (
        <span style={{ fontWeight: 600, fontSize: isMobile ? "14px" : "16px" }}>
          Quarterly Objectives
        </span>
      ),
      children: (
        <div
          style={{
            padding: isMobile ? "12px" : "16px",
            whiteSpace: "pre-wrap",
            color: "#374151",
            lineHeight: "1.6"
          }}
        >
          <ObjectivesTable text={qObjectives} emptyMessage="No quarterly objectives yet..." />
        </div>
      )
    }
  ];

  const objectivesItems: CollapseProps["items"] = [
    {
      key: "objectives",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: isMobile ? "8px" : "12px"
          }}
        >
          <Rocket
            style={{ fontSize: isMobile ? "18px" : "20px", color: "#5B4ECF" }}
          />
          <span
            style={{ fontWeight: 600, fontSize: isMobile ? "16px" : "20px" }}
          >
            Objectives
          </span>
        </div>
      ),
      children: (
        <Collapse
          items={nestedObjectivesItems}
          defaultActiveKey={[
            "peakObjectives",
            "threeYear",
            "oneYear",
            "quarterly"
          ]}
          bordered={false}
          style={{ backgroundColor: "transparent" }}
          expandIcon={({ isActive }) =>
            isActive ? <ChevronDown /> : <ChevronRight />
          }
          expandIconPosition="end"
        />
      )
    }
  ];

  return (
    <>
      <style>
        {`
          .ant-collapse-header {
            display: flex !important;
            align-items: center !important;
          }
        `}
      </style>
      {shouldDisplayLoader ? (
        <VojomoLoader />
      ) : (
        <div style={{ padding: isMobile ? "12px" : "24px", minHeight: "100%" }}>
          <Collapse
            items={strategicItems}
            defaultActiveKey={[]}
            // defaultActiveKey={["strategic"]} // Default to expanded
            style={{
              marginBottom: isMobile ? "16px" : "24px",
              backgroundColor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px"
            }}
            expandIcon={({ isActive }) =>
              isActive ? <ChevronDown /> : <ChevronRight />
            }
            expandIconPosition="end"
            bordered={false}
          />

          <Collapse
            items={objectivesItems}
            defaultActiveKey={[]}
            // defaultActiveKey={["objectives"]} // Default to expanded
            style={{
              backgroundColor: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "8px"
            }}
            expandIcon={({ isActive }) =>
              isActive ? <ChevronDown /> : <ChevronRight />
            }
            expandIconPosition="end"
            bordered={false}
          />
        </div>
      )}
    </>
  );
};
